<template>
    <b-modal :visible="visible && bases?.length > 0" size="sm" scrollable centered no-close-on-backdrop no-close-on-esc hide-footer hide-header-close :title="$t('DbSelection')" title-class="text-primary" header-class="border-0 pb-0" body-class="pb-2"><!--  Sélectionnez une base -->
        <template #modal-header="{close}">
            <b-row class="w-100 p-0 m-0 h-100" align-v="center">
                <b-col class="text-center">
                    <h5 class="text-primary font-weight-bold ">{{ $t('DbSelection') }}</h5><!--  Sélectionnez une base -->
                </b-col>
                <b-col v-if="ChangeBase" cols="auto" class="pr-0 p-1 text-right">
                    <b-icon role="button" icon="x-lg" font-scale="1.5" @click="close(); visible = false; bases = null" v-b-tooltip.hover :title="$t('Close')" /><!-- Fermer -->
                </b-col>
            </b-row>
        </template>
    
        <!-- Liste des bases de données -->
        <b-input-group v-for="base in bases" :key="base.Id" class="w-100">
            <b-button @click="connectBase(base.Id)" class="bg-gray text-black border-0 mb-1 pl-3 py-1 pr-0 d-flex flex-fill justify-content-start align-items-center text-truncate">
                {{ base.Label }}
            </b-button>

            <template #append>
                <div :id="$id(base.Id.toString())">
                    <b-button v-if="isSupervisor && base.Id != 0"
                        @click.stop="openImpersonatePanel(base.Id)"                    
                        class="text-white bg-secondary rounded-start-0 mb-1 py-1 px-1"
                        :disabled="!base.Extras['hasCommercialRight']">
                            <font-awesome-icon icon="user-ninja"/>
                    </b-button>

                <!-- Tooltip indiquant si on peut ajouter ou non un sous-filtre -->
                <b-tooltip :target="$id(base.Id.toString())" noninteractive placement="top"  triggers="hover" >
                    {{ base.Extras['hasCommercialRight'] ? $t('UserImpersonate') : $t('NoCommercialRight') }}
                </b-tooltip>
                </div>
            </template>
        </b-input-group>

        

        <!-- Liste des clients -->
        <ClientList v-if="bDisplayImpersonatePanel" :Visible.sync="bDisplayImpersonatePanel" :ConnectionId="connectionId" :ChangeBase="ChangeBase" :Language="Language" /> 

        <div id="iframeSync" class="d-none">
            <iframe :src="iframeTarget" @load="iframeLoaded" style="border: none"></iframe>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { API_URL } from '@/App.settings';
import { Getter } from 'vuex-class';
import { mixins } from 'vue-class-component';
import { Component, PropSync, Vue } from 'vue-property-decorator';
import axios from 'axios';
import locales from "./locales/ConnexionList.json";
import connection from '@/types/connection';
import Dictionary from '@/types/Dictionary';
import ClientList from './ClientList.vue';
import { I18nUtils, VueSyncHandler } from './Utils';

/** Liste des connexions disponibles */
@Component({
    components: {
        ClientList
    },
    i18n: { messages: locales }
})
export default class ConnexionList extends mixins(VueSyncHandler, I18nUtils) {
    /** Indique si le composant est affiché ou non */
    @PropSync("Visible", {default : () => false})
    public visible : boolean;

    /** Indication si superviseur */
    @Getter("SessionStore/GetUserIsSupervisor")
    private isSupervisor!: boolean;

    private bDisplayImpersonatePanel: boolean = false;
    private connectionId: any[] = [];

    private bases: connection[] | null = null;

    /**
     * Permet de se connecter à une base spécifique
     * @param baseId identifiant de la base
     */
    private async connectBase(baseId : number){
        this.$store.dispatch("ShowLoadingMask", this.$t('ConnectionInProgress'));   // "Connexion en cours"

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";
        args["ConnectionId"] = baseId;

        await axios.post(API_URL + "Connexion/GoBase", args).then(response => {
            if(response.data){
                console.debug("ConnexionList - connectBase - response.data", response.data);

                this.$store.dispatch("ShowLoadingMask", this.$t('DatabaseSync'));   // "Synchronisation de la base..."

                if(response.data.connections[0].Id != 0) {
                    console.debug("ConnexionList - connectBase - data.connections[0] : %O", response.data.connections[0]);
                    Vue.set(this, "redirect", "/Master");
                }
                else
                    Vue.set(this, "redirect", "/Supervision");

                this.handleMessage(response.data.iK, response.data.access_token);
            }
        }).catch(err => {
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                    title: this.$t('Error').toString(),
                    autoHideDelay: 10000,
                    variant: "danger",
                    solid: true,
                    appendToast: true,
                    toaster: "b-toaster-top-center"
                });
        }).finally(() => {
            this.$store.dispatch("HideLoadingMask", {});
        });
    }

    /**
     * Permet l'ouverture de la fenetre de connexion à un utilisateur d'une base
     * @param baseId
     */
    private openImpersonatePanel(baseId: number){
        Vue.set(this, "connectionId", baseId);
        Vue.set(this, "bDisplayImpersonatePanel", true);
    }

    /** Permet de charger une redirection quand l'iframe a changé */
    iframeLoaded() {
        console.debug("ConnexionList - IFrameLoaded - iFrameTarget : %O - redirect", this.iframeTarget, this.redirect);
    }

    created(){
        console.debug("ConnexionList - created !!!");

        /** Gère la langue des textes affichés dans le composant */
        this.handleComponentLocale();

        // Récupération de la liste des bases
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        axios.post(API_URL + "Connexion/GetBaseList", args).then((response) => {
                if(response.data)
                    this.bases = response.data;
            }).catch(err => {
                console.error(err);
                this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { // "Une erreur s'est produite."
                        title: this.$t('Error').toString(),
                        autoHideDelay: 10000,
                        variant: "danger",
                        solid: true,
                        appendToast: true,
                        toaster: "b-toaster-top-center"
                    });
            }).finally(() => {
                this.$store.dispatch("HideLoadingMask", {});
            });
    }

    beforeDestroy() {
        if (this.unwatchLanguage)
            this.unwatchLanguage();
    }
}
</script>

<style scoped>
</style>